import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CButton, CBadge } from "@coreui/react";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

import TNTable from "@/components/TNTable";
import CustomDropdown from "@/components/CustomDropdown";
import ReadMore from "@/components/ReadMore";
import { useTranslationContext } from "@/context/TranslationContext";
import { notification as notificationTranslations } from "@/constants/translation/notification.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import {
  useListNotification,
  useReadOneNotification,
  useReadAllNotifications,
  useDeleteAllNotifications,
} from "@/hooks/useNotification";
import { dateTimeFormat } from "@/utils/dateFormate";
import constants from "@/libs/constant";
import { notification as notificationRoutes } from "@/libs/routes";

const notificationT = notificationTranslations.list;
const { pagination: paginationConstants } = constants;

const filterInputStyle = {
  backgroundColor: "#212631",
  borderColor: "#212631",
  color: "white",
};

const TruncateCell = ({ text, className = "notifications-cell-text" }) => (
  <span className={className} title={text}>
    {text}
  </span>
);

const NotificationList = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isReadFilter, setIsReadFilter] = useState("");
  const [tableKey, setTableKey] = useState(0);

  const isFiltering = useRef(false);

  const { data: response, isFetching } = useListNotification(
    {
      page: currentPage,
      limit: paginationConstants.defaultPageSize,
      ...(isReadFilter !== "" && { is_read: isReadFilter }),
    },
    null,
    () => toast.error(t(notificationT.messages.fetch_failed))
  );

  const { mutate: doReadOne } = useReadOneNotification(() => {
    toast.success(t(notificationT.messages.read_success));
  });

  const { mutate: doReadAll } = useReadAllNotifications(() => {
    toast.success(t(notificationT.messages.read_all_success));
  });

  const { mutate: doDeleteAll } = useDeleteAllNotifications(() => {
    toast.success(t(notificationT.messages.delete_all_success));
    setCurrentPage(1);
  }, () => toast.error(t(notificationT.messages.delete_all_failed)));

  useEffect(() => {
    setLoading(isFetching);

    if (!response) return;

    const list = response?.data?.notifications || [];
    // is_read is a boolean at the API/DB layer, but the app's encrypt/decrypt
    // round-trip coerces booleans to the strings "true"/"false" — both of
    // which are truthy in JS — so normalize it back to a real boolean here.
    setNotifications(list.map((item) => ({ ...item, is_read: item.is_read === true || item.is_read === "true" })));

    const paginationData = response?.data?.pagination;
    if (paginationData) {
      const perPage = parseInt(paginationData.limit, 10) || paginationConstants.defaultPageSize;
      const total = parseInt(paginationData.total, 10) || 0;

      setPagination({
        total,
        per_page: perPage,
        count: total,
        page: parseInt(paginationData.page, 10) || currentPage,
        total_pages: parseInt(paginationData.total_pages, 10) || Math.ceil(total / perPage) || 1,
      });
    } else {
      setPagination(null);
    }
  }, [response, isFetching, currentPage]);

  const statusOptions = useMemo(
    () =>
      constants.status.notificationStatusOptions.map((option) => ({
        ...option,
        label: t(option.label),
      })),
    [t]
  );

  const handleFilterChange = (e) => {
    isFiltering.current = true;
    setIsReadFilter(e.target.value);
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);
    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleRowClick = (row) => {
    if (!row.is_read) {
      doReadOne(row.id);
    }
    if (row.action_url) {
      navigate(row.action_url);
    }
  };

  const handleReadAll = () => {
    doReadAll();
  };

  const handleDeleteAll = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(notificationT.delete_all_popup.heading)}</h2>
            <p className="text-center mb-3">{t(notificationT.delete_all_popup.message)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doDeleteAll();
                  onClose();
                }}
              >
                {t(notificationT.delete_all_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(notificationT.delete_all_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const getTypeLabel = (type) => {
    const key = notificationT.type_label[type];
    return key ? t(key) : type || t(notificationT.defaults.na);
  };

  const columns = useMemo(
    () => [
      {
        id: "notification_date",
        header: t(notificationT.table.date_col_heading),
        accessorKey: "notification_date",
        cell: ({ row }) => (
          <TruncateCell text={dateTimeFormat(row.original.notification_date) || t(notificationT.defaults.na)} />
        ),
      },
      {
        id: "notification_type",
        header: t(notificationT.table.type_col_heading),
        accessorKey: "notification_type",
        cell: ({ row }) => <CBadge color="info">{getTypeLabel(row.original.notification_type)}</CBadge>,
      },
      {
        id: "title",
        header: t(notificationT.table.title_col_heading),
        accessorKey: "title",
        cell: ({ row }) => (
          <span
            role="button"
            onClick={() => handleRowClick(row.original)}
            className="fw-semibold"
            style={{ cursor: "pointer", textDecoration: row.original.action_url ? "underline" : "none" }}
          >
            {row.original.title || t(notificationT.defaults.na)}
          </span>
        ),
      },
      {
        id: "message",
        header: t(notificationT.table.message_col_heading),
        accessorKey: "message",
        cell: ({ row }) => (
          <ReadMore maxLength={30}>{row.original.message || t(notificationT.defaults.na)}</ReadMore>
        ),
      },
      {
        id: "is_read",
        header: t(notificationT.table.status_col_heading),
        accessorKey: "is_read",
        cell: ({ row }) => (
          <CBadge color={row.original.is_read ? "secondary" : "success"}>
            {row.original.is_read ? t(notificationT.status.read) : t(notificationT.status.unread)}
          </CBadge>
        ),
      },
    ],
    [t]
  );

  return (
    <div className="notifications-list">
      <div className="mb-4 d-flex gap-2 justify-content-end flex-wrap">
        <CustomDropdown
          className="filter-field-space form-control-sm"
          style={{ maxWidth: "180px", ...filterInputStyle }}
          value={isReadFilter}
          onChange={handleFilterChange}
          options={statusOptions}
          placeholder={t(notificationT.filter.placeholder)}
        />

        <CButton color="primary" type="button" onClick={handleReadAll} style={filterInputStyle}>
          {t(notificationT.read_all_btn_text)}
        </CButton>

        <CButton color="danger" type="button" onClick={handleDeleteAll}>
          {t(notificationT.delete_all_btn_text)}
        </CButton>
      </div>

      <TNTable
        key={tableKey}
        columns={columns}
        data={notifications}
        paginationData={pagination}
        onSelectPage={(pageIndex) => {
          if (isFiltering.current) return;

          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        pageIndexGet={currentPage - 1}
        isLoading={loading}
        t={t}
        responsive={false}
        className="notifications-table"
      />
    </div>
  );
};

NotificationList.breadCrumbArray = [
  {
    name: breadcrumb_key.notification?.list,
    pathname: notificationRoutes.list,
    active: true,
  },
];

export default NotificationList;
